import { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { isNumber } from 'lodash';
import { useMediaQuery } from '@mui/material';
import { useRouter } from 'next/router';
import { trackEvent } from '@surfline/web-common';

import type { ConditionData, ConditionRating, RatingDays } from 'types/conditionRating';
import type { Weather, WeatherDetail } from 'types/weather';
import type { WindData, WindTimestep } from 'types/wind';
import type { SurfDays, SwellsDays, WeatherDays, WindDays } from 'types/graphs';
import type { ConsistencyData } from 'types/consistency';
import type { SurfData, SurfTimestep } from 'types/surf';
import type { SwellsData, SwellsTimestep } from 'types/swell';
import midnightOfDate from 'utils/midnightOfDate';
import {
  formatTimestampInTimezone,
  hoursAllDay,
  hoursThreePerDay,
  hoursThreeHourly,
  oneDaySeconds,
  oneHourSeconds,
} from 'utils/time';

export const useMaxWidthLandscapeMobile = () => useMediaQuery('(max-width: 812px)');

export interface UseTableDataProps {
  numDaysToDisplay: number;
  ratings: ConditionData;
  consistency: ConsistencyData;
  surf: SurfData;
  swells: SwellsData;
  trackingDetails?: {
    spotName: string;
    spotId: string;
    subregionName: string;
    subregionId: string;
  };
  utcOffset: number;
  weather: Weather;
  wind: WindData;
}

type DaysData = RatingDays | SurfDays | SwellsDays | WindDays | WeatherDays | undefined;
type HourData =
  | ConditionRating
  | SurfTimestep
  | SwellsTimestep
  | WeatherDetail
  | WindTimestep
  | undefined;

const useTableData = ({
  numDaysToDisplay,
  ratings,
  consistency,
  surf,
  swells,
  trackingDetails,
  utcOffset,
  weather,
  wind,
}: UseTableDataProps) => {
  const initialDaysExpanded = new Array(numDaysToDisplay).fill(false);
  // set an expanded day if initialStartDayIndex exists on load
  const [daysExpanded, setDaysExpanded] = useState(initialDaysExpanded);
  const [showHourlyWhenExpanded, setShowHourlyWhenExpanded] = useState(false);
  const router = useRouter();
  const prevInitialStartDayIndexRef = useRef<string | undefined>(undefined);

  const toggleDayExpanded = useCallback(
    (dayIndex: number) => {
      if (!daysExpanded[dayIndex]) {
        trackEvent('Forecast Range Updated', {
          pageName: 'Spot Page',
          uiElement: 'table view',
          action: 'click',
          forecastStart: dayIndex,
          forecastEnd: dayIndex,
          ...trackingDetails,
        });
      }
      const newDaysExpanded = [...daysExpanded];
      newDaysExpanded[dayIndex] = !newDaysExpanded[dayIndex];
      setDaysExpanded(newDaysExpanded);
    },
    [daysExpanded, trackingDetails],
  );

  const toggleDayHourlyExpanded = useCallback((_dayIndex: number) => {
    setShowHourlyWhenExpanded((prev) => !prev);
  }, []);

  const filterHours = useCallback(
    (day: any, dayIndex: number) =>
      Array.isArray(day)
        ? day.filter((hour: HourData) => {
            // Format 'H'	= 0, 1, 2, ..., 23
            const hourFormatted = hour?.timestamp
              ? formatTimestampInTimezone(hour.timestamp, hour.utcOffset, 'H')
              : '';

            const hoursToShow = !daysExpanded[dayIndex]
              ? hoursThreePerDay
              : showHourlyWhenExpanded
                ? hoursAllDay
                : hoursThreeHourly;
            const show = hoursToShow.indexOf(hourFormatted) >= 0;
            return show;
          })
        : [],
    [daysExpanded, showHourlyWhenExpanded],
  );

  const filterDays = useCallback(
    (days: DaysData, isLoading: boolean, error?: any) => {
      if (isLoading || error) {
        // Use placeholder days to force loading states while loading or error
        const midnightToday = midnightOfDate(utcOffset);

        const placeholderArray = [...new Array(numDaysToDisplay)].map((_, dayIndex) => {
          const hoursToFill =
            daysExpanded[dayIndex] && showHourlyWhenExpanded
              ? Array.from({ length: 24 }, (_, i) => i)
              : daysExpanded[dayIndex]
                ? [0, 3, 6, 9, 12, 15, 18, 21]
                : [6, 12, 18];
          return hoursToFill.map((hourIndex) => ({
            timestamp: midnightToday + oneHourSeconds * hourIndex + oneDaySeconds * dayIndex,
            utcOffset,
          }));
        });
        return placeholderArray;
      }

      return [...(days || []).map((day, dayIndex) => filterHours(day, dayIndex))]?.slice(
        0,
        numDaysToDisplay,
      );
    },
    [daysExpanded, showHourlyWhenExpanded, filterHours, numDaysToDisplay, utcOffset],
  );

  const ratingsDays = useMemo(
    () => filterDays(ratings?.days, ratings?.loading, ratings?.error),
    [filterDays, ratings?.days, ratings?.error, ratings?.loading],
  );

  const consistencyDays = useMemo(
    () => filterDays(consistency?.days ?? [], consistency?.loading, consistency?.error),
    [filterDays, consistency?.days, consistency?.error, consistency?.loading],
  );

  const surfDays = useMemo(
    () => filterDays(surf?.days, surf?.loading, surf?.error),
    [filterDays, surf?.days, surf?.error, surf?.loading],
  );

  const swellsDays = useMemo(
    () => filterDays(swells?.days, swells?.loading, swells?.error),
    [filterDays, swells?.days, swells?.error, swells?.loading],
  );

  const weatherDays = useMemo(
    () => filterDays(weather?.days, weather?.loading, weather?.error),
    [filterDays, weather?.days, weather?.error, weather?.loading],
  );

  const windDays = useMemo(
    () => filterDays(wind?.days, wind?.loading, wind?.error),
    [filterDays, wind?.days, wind?.error, wind?.loading],
  );

  // set page scroll if initialStartDayIndex is updated
  useEffect(() => {
    const initialStartDayIndex = router?.query?.initialStartDayIndex;
    const prevInitialStartDayIndex = prevInitialStartDayIndexRef.current;

    // Only expand day if initialStartDayIndex has actually changed
    if (
      initialStartDayIndex !== prevInitialStartDayIndex &&
      initialStartDayIndex &&
      !isNumber(initialStartDayIndex) &&
      Number(initialStartDayIndex) >= 0 &&
      Number(initialStartDayIndex) <= 15
    ) {
      const dayIndex = Number(initialStartDayIndex);
      setDaysExpanded((prevDaysExpanded) => {
        const newDaysExpanded = [...prevDaysExpanded];
        if (newDaysExpanded?.[dayIndex] !== null) {
          newDaysExpanded[dayIndex] = true;
        }
        return newDaysExpanded;
      });
    }

    // Update the ref with the current value
    prevInitialStartDayIndexRef.current = Array.isArray(initialStartDayIndex)
      ? initialStartDayIndex[0]
      : initialStartDayIndex;
  }, [router?.query?.initialStartDayIndex]);

  return {
    daysExpanded,
    showHourlyWhenExpanded,
    ratingsDays,
    consistencyDays,
    toggleDayExpanded,
    toggleDayHourlyExpanded,
    surfDays,
    swellsDays,
    weatherDays,
    windDays,
  };
};

export default useTableData;
